import { useState } from "react";

import { Destination } from "Constant/destination";

import {
    createGoogleEnhanceMapping,
    createGoogleWebMapping,
    sendGoogleEnhancedAccount,
    sendGoogleEnhancedData,
} from "helpers/destinations/createDestination";

import useHostFooterAction from "components/organisms/CreationModal/hooks/useHostFooterAction";
import SwalAlertComp from "components/SwalAlert";

import { GOOGLE_ENHANCED_TYPES } from "../DestinationCreation.constants";

/**
 * Google Offline (ggads) / Web (ggads_web) Conversions: name, Google login,
 * account + event mapping (`GoogleOfflinePixels`).
 */
const useGoogleEnhancedDestination = ({
    language,
    destinationCode,
    setSaveDisabled,
    setModalLoading,
    resetForm,
    completeCreation,
    registerFooterAction,
    isSaveDisabled,
}) => {
    const [name, setName] = useState(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isAuthLoading, setIsAuthLoading] = useState(false);
    const [accountsData, setAccountsData] = useState([]);
    const [destinationId, setDestinationId] = useState("");
    const [selectedAccounts, setSelectedAccounts] = useState(null);
    const [mapping, setMapping] = useState(null);

    const failedTitle = language?.delete_swal_modal?.failed ?? "Failed";

    const resetOnTypeChange = () => {
        setAccountsData([]);
        setDestinationId(null);
        setIsConnected(false);
        setName("");
        setSelectedAccounts(null);
        setMapping(null);
    };

    const handleNameChange = (event) => {
        setName(event.target.value);
    };

    const handleLoginSuccess = async (data) => {
        const googleCode = data?.google_code;
        setIsAuthLoading(true);
        const response = await sendGoogleEnhancedData({
            pixel_id: name && name,
            destination_type:
                destinationCode === Destination.ggads_web
                    ? Destination.ggads_web
                    : Destination.ggads,
            google_code: googleCode && googleCode,
        });
        setIsAuthLoading(false);

        if (response.status) {
            setAccountsData(response?.data?.accounts);
            setDestinationId(response?.data?.destination_id);
            setIsConnected(true);
            setSaveDisabled(false);
        } else {
            SwalAlertComp({
                title: failedTitle,
                message: response.message && response.message,
                icon: "error",
            });
        }
    };

    const showFailure = (message) => {
        SwalAlertComp({ title: failedTitle, message, icon: "error" });
        setModalLoading(false);
    };

    const create = async ({ createMapping, getSuccessMessage }) => {
        setModalLoading(true);

        const response = await sendGoogleEnhancedAccount({
            destination_id: destinationId,
            account_id: selectedAccounts,
        });
        if (!response.status) {
            showFailure(response.message);
            return;
        }

        const mappingResponse = await createMapping(destinationId, mapping);
        if (!mappingResponse.status) {
            showFailure(mappingResponse.message);
            return;
        }

        SwalAlertComp({
            title: language?.delete_swal_modal?.success ?? "Success",
            message: getSuccessMessage(response),
            icon: "success",
            timer: 3000,
        });
        setIsConnected(false);
        setName("");
        resetForm();
        await completeCreation([response?.data?.id]);
    };

    const handleCreate = () =>
        destinationCode === Destination.ggads_web
            ? create({
                  createMapping: createGoogleWebMapping,
                  getSuccessMessage: (response) => response.message,
              })
            : create({
                  createMapping: createGoogleEnhanceMapping,
                  getSuccessMessage: () => "Destination created successfully",
              });

    const isActive = GOOGLE_ENHANCED_TYPES.includes(destinationCode);

    useHostFooterAction(
        isActive ? registerFooterAction : undefined,
        `${destinationCode}:form`,
        {
            label:
                language?.buttons?.create_destination ?? "Create destination",
            onSubmit: handleCreate,
            isDisabled: isSaveDisabled,
            hidden: !isConnected,
        },
    );

    return {
        name,
        handleNameChange,
        isConnected,
        isAuthLoading,
        accountsData,
        setSelectedAccounts,
        setMapping,
        resetOnTypeChange,
        handleLoginSuccess,
        handleCreate,
    };
};

export default useGoogleEnhancedDestination;
